import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, Typography, Paper, IconButton, Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import emma_w from './../../../../global/imagenes/emma_w.png';
import TareasTerminadas from '../../../general/TareasTerminadas';
import sesionesBienestar from '../../../../global/imagenes/sesionesBienestar.png';
import bienestarFormato from '../../../../global/imagenes/formatosBienestar.png';
import { COD_CRISIS, COD_CULTURA_C } from '../../helper';
import {
    extraerSesionNumero,
    hasSessionData,
    isSessionEnabledForStudent,
} from '../helpers/sesionesFormatosUtils';

const CARD_STYLES = {
    card: {
        maxWidth: '100%',
        width: '100%',
        minHeight: 180,
        height: '100%',
        cursor: 'pointer',
        borderRadius: 10,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    cardHeader: { fontSize: '0.84rem', fontWeight: 700, textAlign: 'center', color: '#111', padding: '6px 8px 3px', lineHeight: 1.2, minHeight: 40 },
    cardImage: { width: 64, height: 64, objectFit: 'cover', display: 'block', margin: '0 auto' },
    cardDescription: { fontSize: '0.76rem', lineHeight: '1.25', color: '#666', margin: '6px 8px 0px 8px', textAlign: 'center' },
    gridItem: { padding: '6px' },
    container: {
        display: 'grid',
        gap: 12,
        justifyItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        padding: '0 8px',
    },
    itemWrapper: { width: '100%', display: 'flex', justifyContent: 'center', minWidth: 0 },
};

// Función para determinar si un módulo está completado
const estaModuloCompletado = (moduleKey, formulariosDeEstaSesion) => {
    switch (moduleKey) {
        case 'asistencia':
            return !!formulariosDeEstaSesion.asistencia;
        case 'satisfaccion':
            return !!formulariosDeEstaSesion.satisfaccion;
        case 'consentimiento':
            return !!formulariosDeEstaSesion.consentimientoGeneral;
        case 'consentimiento_crisis':
            return !!formulariosDeEstaSesion.consentimiento_crisis;
        default:
            return false;
    }
};
 


const FormatoSesiones = ({
    sesiones = [],
    onModuleSelect = () => {},
    loading = false,
    onSessionOpen = null,
    formulariosCompletados = {},
    modoLectura = false,
    onVerDetalle = null,
    sesionInicial = null,
    tipoServicio = null,
    sesionesBloqueoSecuencial = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedSesion, setSelectedSesion] = useState(null);
    const [loadingSession, setLoadingSession] = useState(false);
    const [minLoader, setMinLoader] = useState(true);
    const readyToRender = !loading && sesiones.length > 0 && tipoServicio != null && !minLoader;

    useEffect(() => {
        setMinLoader(true);
        const t = setTimeout(() => setMinLoader(false), 500);
        return () => clearTimeout(t);
    }, [loading, sesiones.length, tipoServicio]);

    useEffect(() => {
        if (!sesionInicial) {
            setSelectedSesion(null);
            return;
        }
        if (modoLectura && !hasSessionData(sesionInicial, formulariosCompletados)) {
            setSelectedSesion(null);
            return;
        }
        setSelectedSesion(sesionInicial);
    }, [sesionInicial, modoLectura, formulariosCompletados]);

    const sesionesOrdenadas = useMemo(() => {
        return sesiones.slice().sort((a, b) => {
            const getNum = x => {
                if (!x) return NaN;
                if (x.valorc !== undefined && x.valorc !== null && x.valorc !== '') { const n = parseFloat(x.valorc); if (!isNaN(n)) return n; }
                if (x.valor !== undefined && x.valor !== null && x.valor !== '') { const n = parseFloat(x.valor); if (!isNaN(n)) return n; }
                if (x.orden !== undefined && x.orden !== null && x.orden !== '') { const n = parseFloat(x.orden); if (!isNaN(n)) return n; }
                if (x.id !== undefined && x.id !== null && x.id !== '') { const n = parseFloat(x.id); if (!isNaN(n)) return n; }
                return NaN;
            };
            const na = getNum(a);
            const nb = getNum(b);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            const sa = (a.nombre || a.valor || a.valorc || '').toString().toLowerCase();
            const sb = (b.nombre || b.valor || b.valorc || '').toString().toLowerCase();
            return sa.localeCompare(sb);
        });
    }, [sesiones]);

    const requiereFiltro = tipoServicio?.codigo === COD_CRISIS || tipoServicio === COD_CULTURA_C;
    const sesionesFiltradas = useMemo(() => {
        if (requiereFiltro) {
            return sesionesOrdenadas.filter(s => s.codigo === 'pri_se_bien');
        }
        return sesionesOrdenadas;
    }, [sesionesOrdenadas, requiereFiltro]);


    const openSession = useCallback((s) => {
        if (modoLectura) {
            if (!hasSessionData(s, formulariosCompletados)) return;
        } else {
            const n = extraerSesionNumero(s);
            if (!isSessionEnabledForStudent(n, sesionesFiltradas, formulariosCompletados, sesionesBloqueoSecuencial)) {
                return;
            }
        }
        setSelectedSesion(null);
        if (onSessionOpen && typeof onSessionOpen === 'function') {
            try {
                const maybePromise = onSessionOpen(s);
                if (maybePromise && typeof maybePromise.then === 'function') {
                    setLoadingSession(true);
                    maybePromise.then(() => setSelectedSesion(s)).catch(() => setSelectedSesion(s)).finally(() => setLoadingSession(false));
                    return;
                }
            } catch (err) {
                // Error silencioso
            }
        }
        setLoadingSession(true);
        setTimeout(() => { setSelectedSesion(s); setLoadingSession(false); }, 220);
    }, [onSessionOpen, sesionesBloqueoSecuencial, sesionesFiltradas, formulariosCompletados, modoLectura]);

    // Extrae datos de sesión seleccionada
    const sesionNumeroCheck = selectedSesion ? extraerSesionNumero(selectedSesion) : NaN;
    const formsSesion = formulariosCompletados[sesionNumeroCheck] || {};
    const tieneCrisisCompletado = !!formsSesion.consentimiento_crisis;
    const sessCodigoCheck = selectedSesion?.codigo;

    // Memoriza los módulos consentimiento
    const consentimientoModules = useMemo(() => {
        if (!selectedSesion) return [];
        return tieneCrisisCompletado 
            ? [{ key: 'consentimiento_crisis', title: 'Consentimiento' }]
            : (sessCodigoCheck === 'pri_se_bien' ? [{ key: 'consentimiento', title: 'Consentimiento' }] : []);
    }, [tieneCrisisCompletado, sessCodigoCheck, selectedSesion]);

    const CONTENIDO_MIN_ALTURA = 420;
    const columnasGrid = isMobile ? 1 : (isTablet ? 2 : 3);
    const containerStyle = { ...CARD_STYLES.container, gridTemplateColumns: `repeat(${columnasGrid}, minmax(0, 1fr))` };

    if (!readyToRender) return (
        <div style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: CONTENIDO_MIN_ALTURA, width: '100%', boxSizing: 'border-box' }}>
            <TareasTerminadas mensaje="Cargando sesiones" imagen={emma_w} widthImg="7%" cargando />
        </div>
    );
 
    if (!sesiones || sesiones.length === 0) {
        return (
            <div style={{ padding: 24, minHeight: CONTENIDO_MIN_ALTURA, width: '100%', boxSizing: 'border-box' }}>
                <Typography>No hay sesiones definidas</Typography>
            </div>
        );
    }

    if (selectedSesion) {
        const sess = selectedSesion;
        if (loadingSession) return (
            <div style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: CONTENIDO_MIN_ALTURA, width: '100%', boxSizing: 'border-box' }}>
                <TareasTerminadas mensaje={'Cargando sesión'} imagen={emma_w} widthImg={'7%'} cargando={true} />
            </div>
        );
        
        const modules = [
            { key: 'asistencia', title: 'Asistencia' },
            ...consentimientoModules,
            { key: 'satisfaccion', title: 'Satisfacción' }
        ];
 
        return (
            <div style={{ position: 'relative', padding: 12, minHeight: CONTENIDO_MIN_ALTURA, width: '100%', boxSizing: 'border-box' }}>
                <div style={{ position: 'absolute', left: 4, top: 2 }}>
                    <IconButton
                        aria-label="Volver a sesiones"
                        color="primary"
                        size="small"
                        onClick={() => setSelectedSesion(null)}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                    <Typography variant='h6' style={{ marginLeft: 8 }}>{sess.nombre || sess.valor}</Typography>
                </div>
                <div style={containerStyle}>
                {modules.map(m => {
                    const sesionNumero = extraerSesionNumero(sess);
                    const formulariosDeEstaSesion = formulariosCompletados[sesionNumero] || {};
                    const isCompleted = estaModuloCompletado(m.key, formulariosDeEstaSesion);
                    const handleClick = () => {
                        if (modoLectura) {
                            if (isCompleted && onVerDetalle) {
                                onVerDetalle(m.key, sess);
                            }
                        } else {
                            if (!isCompleted && onModuleSelect) {
                                onModuleSelect(m.key, sess);
                            }
                        }
                    };

                    return (
                    <div key={m.key} style={Object.assign({}, CARD_STYLES.itemWrapper, CARD_STYLES.gridItem)}>
                    <Paper style={{ borderRadius: 10, width: '100%' }} elevation={0}>
                        <Card 
                            style={{
                                ...CARD_STYLES.card,
                                cursor: (modoLectura ? isCompleted : !isCompleted) ? 'pointer' : 'default',
                                opacity: (modoLectura && !isCompleted) || (!modoLectura && isCompleted) ? 0.5 : 1,
                                pointerEvents: (modoLectura && !isCompleted) || (!modoLectura && isCompleted) ? 'none' : 'auto',
                                filter: (modoLectura && !isCompleted) || (!modoLectura && isCompleted) ? 'grayscale(50%)' : 'none'
                            }} 
                            elevation={2} 
                            onClick={handleClick}
                            onMouseEnter={e => (modoLectura ? isCompleted : !isCompleted) && (e.currentTarget.style.transform = 'translateY(-4px)')} 
                            onMouseLeave={e => (modoLectura ? isCompleted : !isCompleted) && (e.currentTarget.style.transform = 'translateY(0)')}
                        >
                        <CardHeader title={m.title} titleTypographyProps={{ style: CARD_STYLES.cardHeader }} />
                            <CardContent style={{ padding: '8px 10px 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
                            <div style={{ width: '100%', height: 58, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative' }}>
                                <img src={bienestarFormato} alt={m.title} style={CARD_STYLES.cardImage} />
                                {isCompleted && !modoLectura && (
                                    <div style={{ position: 'absolute', top: 0, right: 0 }}>
                                        <CheckCircleIcon style={{ color: '#6cf871ff', fontSize: 28 }} />
                                    </div>
                                )}
                            </div>
                        </CardContent>
                        </Card>
                    </Paper>
                    </div>
                    );
                })}
                </div>
            </div>
        );
    }
 
    return (
        <div style={{ width: '100%', minHeight: CONTENIDO_MIN_ALTURA, boxSizing: 'border-box', padding: '4px 0 8px' }}>
        <div style={containerStyle}>
        {sesionesFiltradas.map((s, i) => {
            const img = s.archivo || s.imagen || s.icono || s.valor || s.url || sesionesBienestar;
            const numSesion = extraerSesionNumero(s);
            const sesionBloqueada = modoLectura
                ? !hasSessionData(s, formulariosCompletados)
                : !isSessionEnabledForStudent(numSesion, sesionesFiltradas, formulariosCompletados, sesionesBloqueoSecuencial);
            const mostrarLeyendaSecuencialEstudiante =
                !modoLectura &&
                sesionesBloqueoSecuencial &&
                !isSessionEnabledForStudent(numSesion, sesionesFiltradas, formulariosCompletados, sesionesBloqueoSecuencial);
            return (
            <div key={s.id || i} style={Object.assign({}, CARD_STYLES.itemWrapper, CARD_STYLES.gridItem)}>
                <Paper style={{ borderRadius: 10, width: '100%' }} elevation={0}>
                <Card
                    style={{
                        ...CARD_STYLES.card,
                        cursor: sesionBloqueada ? 'not-allowed' : 'pointer',
                        opacity: sesionBloqueada ? 0.45 : 1,
                        filter: sesionBloqueada ? 'grayscale(35%)' : 'none',
                        pointerEvents: sesionBloqueada ? 'none' : 'auto',
                    }}
                    elevation={2}
                    onClick={() => {
                        if (!sesionBloqueada) openSession(s);
                    }}
                    onMouseEnter={(e) => {
                        if (!sesionBloqueada) e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <CardHeader title={s.nombre || s.valor || `Sesión ${i + 1}`} titleTypographyProps={{ style: CARD_STYLES.cardHeader }} />
                    <CardContent
                        style={{
                            padding: '8px 10px 10px',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            justifyContent: 'space-between',
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                    <div style={{ width: '100%', height: 58, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexShrink: 0 }}>
                        <img src={img} alt={s.nombre || ''} style={CARD_STYLES.cardImage} />
                    </div>
                    {s.descripcion && (
                        <Typography variant="body2" style={{ ...CARD_STYLES.cardDescription, flexShrink: 0 }}>{s.descripcion}</Typography>
                    )}
                    {mostrarLeyendaSecuencialEstudiante && (
                        <Box
                            mt="auto"
                            pt={1}
                            px={0.5}
                            pb={0.25}
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="center"
                            style={{ gap: 6, borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(25, 118, 210, 0.06)' }}
                        >
                            <LockOutlinedIcon style={{ fontSize: 18, color: 'rgba(25, 118, 210, 0.75)', marginTop: 1, flexShrink: 0 }} />
                            <Typography
                                variant="caption"
                                component="span"
                                style={{
                                    textAlign: 'left',
                                    lineHeight: 1.35,
                                    color: 'rgba(25, 55, 95, 0.82)',
                                    fontWeight: 500,
                                    fontSize: '0.72rem',
                                }}
                            >
                                Completa la sesión anterior para continuar
                            </Typography>
                        </Box>
                    )}
                    </CardContent>
                </Card>
                </Paper>
            </div>
            );
        })}
        </div>
        </div>
    );
};

FormatoSesiones.propTypes = {
    sesiones: PropTypes.array,
    onModuleSelect: PropTypes.func,
    loading: PropTypes.bool,
    onSessionOpen: PropTypes.func,
    formulariosCompletados: PropTypes.object,
    modoLectura: PropTypes.bool,
    onVerDetalle: PropTypes.func,
    tipoSolicitud: PropTypes.string,
    sesionesBloqueoSecuencial: PropTypes.bool,
};

export default FormatoSesiones;